
import { useState, useEffect } from 'react';
import { BusinessDetails } from '../types';
import { GeoLocation } from '@/components/business/onboarding/types/locationTypes';
import { fetchTimeZoneInfo } from '@/utils/timeZoneUtils';
// import { useDataUtils } from '@/hooks/useDataUtils'; // Removed
// Update to use standardized validation utilities that return string | undefined
import { 
  validatePhone, 
  validateEmail
} from '@/components/portfolio/settings/utils/validationUtils';
// validateWebsite can remain if its source is still appropriate and it returns boolean
import { validateWebsite } from '@/utils/validation';


// Define initial state for business details
const initialBusinessDetails: BusinessDetails = {
  name: '',
  shortName: '',
  phone: '',
  email: '',
  industry: '',
  industryVertical: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  website: '',
  founded: '',
  timeZone: '',
  coordinates: undefined,
  handleCoordinatesUpdate: undefined,
  handleEnsureTimeZone: undefined
};

export const useBusinessForm = () => {
  // Helper function to truncate text with ellipsis
  const truncateWithEllipsis = (text: string, maxLength: number = 30): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + "..."; // Reserve 3 chars for ellipsis
  };

  // const { getGoogleMapsApiKey } = useDataUtils(); // Removed
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>(initialBusinessDetails);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [shortNameManuallyEdited, setShortNameManuallyEdited] = useState(false);
  const [timezoneAutoDetected, setTimezoneAutoDetected] = useState(false);
  const [errors, setErrors] = useState<{
    phone?: string;
    email?: string;
    website?: string;
  }>({});
  
  // Calculate if name exceeds 30 chars
  const nameExceeds30Chars = businessDetails.name.length > 30;
  const shortNameLength = businessDetails.shortName.length;
  
  // Update short name when name changes (unless manually edited)
  // Always truncate to 30 characters for display consistency
  useEffect(() => {
    if (!shortNameManuallyEdited && businessDetails.name) {
      setBusinessDetails(prev => ({
        ...prev,
        shortName: truncateWithEllipsis(businessDetails.name)
      }));
    }
  }, [businessDetails.name, shortNameManuallyEdited]);
  
  // Fetch timezone when coordinates change
  useEffect(() => {
    const updateTimeZone = async () => {
      if (businessDetails.coordinates && !timezoneAutoDetected) {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string; // Get API key from env
        if (!apiKey) {
          console.error('No Google Maps API key available (VITE_GOOGLE_MAPS_API_KEY not set)');
          return;
        }
        
        // Ensure coordinates and their properties exist before destructuring
        if (!businessDetails.coordinates || typeof businessDetails.coordinates.latitude !== 'number' || typeof businessDetails.coordinates.longitude !== 'number') {
          console.error('Coordinates or latitude/longitude are missing for timezone lookup');
          return;
        }
        const { latitude, longitude } = businessDetails.coordinates; // Changed from lat, lng
        const timezone = await fetchTimeZoneInfo(latitude, longitude, apiKey); // Pass latitude, longitude
        
        if (timezone) {
          setTimezoneAutoDetected(true);
          setBusinessDetails(prev => ({
            ...prev,
            timeZone: timezone
          }));
        }
      }
    };
    
    if (businessDetails.coordinates) {
      updateTimeZone();
    }
  }, [businessDetails.coordinates, timezoneAutoDetected]);
  
  // Validate fields when they change
  useEffect(() => {
    const newErrors: { phone?: string; email?: string; website?: string } = {};
    
    let phoneError, emailError;

    if (businessDetails.phone) {
      phoneError = validatePhone(businessDetails.phone);
      if (phoneError) newErrors.phone = phoneError;
    }
    
    if (businessDetails.email) {
      emailError = validateEmail(businessDetails.email);
      if (emailError) newErrors.email = emailError;
    }
    
    // validateWebsite from '@/utils/validation' returns boolean
    if (businessDetails.website && !validateWebsite(businessDetails.website)) {
      newErrors.website = "Please enter a valid website URL";
    }
    
    setErrors(newErrors);
  }, [businessDetails.phone, businessDetails.email, businessDetails.website]);
  
  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Set flag if shortName is manually edited
    if (name === 'shortName' && value !== businessDetails.name) {
      setShortNameManuallyEdited(true);
    }
    
    // Ensure shortName is always truncated to 30 characters
    if (name === 'shortName') {
      setBusinessDetails(prev => ({
        ...prev,
        shortName: truncateWithEllipsis(value)
      }));
    } else {
      setBusinessDetails(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle select input changes
  const handleSelectChange = (value: string, field: string) => {
    // If setting timezone directly, mark it as auto-detected to prevent override
    if (field === 'timeZone') {
      setTimezoneAutoDetected(true);
    }
    
    setBusinessDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setCalendarOpen(false);
    
    if (date) {
      const year = date.getFullYear().toString();
      setBusinessDetails(prev => ({
        ...prev,
        founded: year
      }));
    }
  };
  
  // Handle website blur event for final validation and formatting
  const handleWebsiteBlur = () => {
    // Format website URL if needed
    if (businessDetails.website) {
      let formattedWebsite = businessDetails.website.trim();
      
      // Remove any leading "www." if it exists without a protocol
      if (formattedWebsite.startsWith('www.') && 
          !formattedWebsite.startsWith('http://') && 
          !formattedWebsite.startsWith('https://')) {
        formattedWebsite = formattedWebsite.substring(4);
      }
      
      // Add https:// if no protocol is present
      if (!formattedWebsite.startsWith('http://') && 
          !formattedWebsite.startsWith('https://') &&
          formattedWebsite.length > 0) {
        formattedWebsite = 'https://' + formattedWebsite;
      }
      
      // Update the website value with the formatted version (with protocol)
      if (formattedWebsite !== businessDetails.website) {
        console.log('Blur formatted website:', formattedWebsite);
        setBusinessDetails(prev => ({
          ...prev,
          website: formattedWebsite
        }));
      }
    }
  };
  
  // Handle coordinates update
  const handleCoordinatesUpdate = (coordinates: GeoLocation) => {
    // Ensure the incoming coordinates from GeoLocation are numbers before setting
    if (typeof coordinates.latitude === 'number' && typeof coordinates.longitude === 'number') {
      setBusinessDetails(prev => ({
        ...prev,
        coordinates: { // This sets BusinessDetails.coordinates
          latitude: coordinates.latitude,  // from GeoLocation parameter
          longitude: coordinates.longitude // from GeoLocation parameter
        }
      }));
    } else {
      console.warn("handleCoordinatesUpdate received invalid coordinates:", coordinates);
      // Optionally set coordinates to null or don't update
      setBusinessDetails(prev => ({
        ...prev,
        coordinates: undefined // Or null, depending on desired behavior for invalid input
      }));
    }
  };
  
  // Reset business details
  const resetBusinessDetails = () => {
    setBusinessDetails(initialBusinessDetails);
    setSelectedDate(undefined);
    setShortNameManuallyEdited(false);
    setTimezoneAutoDetected(false);
    setErrors({});
  };
  
  // Update business details (used when selecting from search)
  const updateBusinessDetails = (details: Partial<BusinessDetails>) => {
    // If setting timezone in details, mark it as detected
    if (details.timeZone) {
      setTimezoneAutoDetected(true);
    }
    
    // Ensure shortName is truncated if it's being updated
    const updatedDetails = { ...details };
    if (updatedDetails.shortName) {
      updatedDetails.shortName = truncateWithEllipsis(updatedDetails.shortName);
    }
    
    setBusinessDetails(prev => ({
      ...prev,
      ...updatedDetails,
      handleCoordinatesUpdate,
      handleEnsureTimeZone: prev.handleEnsureTimeZone
    }));
  };

  // Add the coordinates update handler to the business details
  useEffect(() => {
    setBusinessDetails(prev => ({
      ...prev,
      handleCoordinatesUpdate,
      handleEnsureTimeZone: prev.handleEnsureTimeZone
    }));
  }, []);

  return {
    businessDetails,
    calendarOpen,
    setCalendarOpen,
    selectedDate,
    setSelectedDate,
    shortNameManuallyEdited,
    nameExceeds30Chars,
    shortNameLength,
    handleInputChange,
    handleSelectChange,
    handleDateSelect,
    handleWebsiteBlur,
    handleCoordinatesUpdate,
    resetBusinessDetails,
    updateBusinessDetails,
    errors
  };
};
